/**
 * §13 — the UI facade. Owns every DOM surface above the canvas: HUD, screens,
 * onboarding overlays, microinteractions, and the settings store.
 *
 * Contract (INTERFACES.md, R8):
 *   new UI(root) → bindWorld(w) → setScreen(name) → update(w) once per rendered frame.
 * `update` never mutates `World`; it reads the fields listed in the sim→presentation
 * read API and writes only to cached DOM nodes.
 */

import { makeCaptain, type Captain } from '../captain/seed';
import type { LineState } from '../captain/lines';
import { bossById, enemyById } from '../core/content';
import type { World } from '../sim/world';
import { CaptainSpeech } from './captain-speech';
import { CaptainVoice, makeDeathLedger } from './captain-voice';
import { div, setClass } from './dom';
import { EventLogStore } from './event-log';
import { humanizeId } from './format';
import { Hud } from './hud';
import type { HudReservedZones } from './layout';
import { Microinteractions } from './microinteractions';
import { NavController } from './nav';
import { Onboarding } from './onboarding';
import { RunObserver } from './observer';
import { SettingsStore } from './settings-store';
import type { StatusFields } from './status-line';
import { ArchiveScreen } from './screens/archive';
import { DefeatScreen } from './screens/defeat';
import { LevelUpScreen } from './screens/levelup';
import { LoadoutScreen, randomSeed } from './screens/loadout';
import { PauseScreen } from './screens/pause';
import { ResumePromptScreen } from './screens/resume-prompt';
import { SettingsScreen } from './screens/settings';
import { SummaryScreen } from './screens/summary';
import { TitleScreen } from './screens/title';
import { VictoryScreen } from './screens/victory';
import type { Screen } from './screens/base';
import type {
  DefeatReport,
  InputMethod,
  LevelUpState,
  ResumeInfo,
  RunSummary,
  ScreenName,
  Settings,
  UIAction,
} from './types';

/** DECISIONS AMD-1: HUD scale keys off the *smaller* viewport axis, floor 0.85. */
const HUD_SCALE_MIN = 0.85;
const HUD_SCALE_MAX = 3.0;
const HUD_SCALE_BASE = 390;

/** §13.1.7 boss intro framing: 3 s of scripted lock, ≤ 3.2 s total. */
export const BOSS_INTRO_MS = 3000;

/** `bosses.json` carries no epithet field; §13.1.7's one-liners live here. */
const BOSS_EPITHETS: Record<string, string> = {
  'boss.lantern-tyrant': 'the deep learns to hunt',
  'boss.abyssal-warden': 'the wall that walks',
  'boss.crown-medusa': 'a thousand drifting knives',
  'boss.hadal-sovereign': 'the pressure with a name',
};

const VERSION = '0.1.0';

/**
 * AMD-55 — how long the defeat panel waits, in wall-clock ms. Covers the rupture and the
 * deceleration in `render/player-death.ts` (0.12 s + 0.88 s); the 2.2 s afterglow keeps
 * burning behind the card.
 */
const DEATH_BEAT_MS = 1000;

export class UI {
  readonly root: HTMLElement;
  readonly hud: Hud;
  readonly micro: Microinteractions;
  readonly onboarding: Onboarding;
  readonly nav = new NavController();
  readonly observer = new RunObserver();
  /** AMD-13 — one recorder, read by the Pause and Run Summary log views. */
  readonly eventLog = new EventLogStore();
  /** The captain's voice. Built once; a run swaps the speaker inside it. */
  readonly speech = new CaptainSpeech();

  private store: SettingsStore;
  private screens = new Map<ScreenName, Screen>();
  private screenLayer: HTMLElement;
  private current: ScreenName = 'title';

  /** Which screen is showing. The renderer reads this to decide whether in-run canvas
   *  chrome — the AMD-8 radar — belongs on the frame at all. */
  get currentScreen(): ScreenName {
    return this.current;
  }
  private started = false;
  private world: World | null = null;
  private unsubs: (() => void)[] = [];
  private inputMethod: InputMethod = 'keyboard';
  private prefersReducedMotion = false;
  private contentVersion = VERSION;
  private lastSeed = 0;
  /** AMD-13 status-line coordinates. `World` carries neither, so the UI tracks both. */
  private lastArena = 'arena.trench-floor';
  private lastPhase = '';
  /** AMD-34 / AMD-35 run totals the summary reads. `World` publishes neither. */
  private lastWave = 1;
  private graftsTaken = 0;
  private ichorEarned = 0;
  /** Tick at the last `bindWorld`, so a re-bind can tell a seek from a restart. */
  private lastBoundTick = -1;
  /**
   * The captain. Derived from `w.runSeed` and nothing else, so a share string
   * still reproduces the run *and* the person flying it. Held for exactly as long as
   * the run: no storage, no snapshot field, no meta save. A dead captain is gone.
   */
  private captain: Captain | null = null;
  private voice: CaptainVoice | null = null;
  /**
   * Session-scoped, in memory only. It exists so the second captain you lose does not
   * repeat the first one's exit line — never so a captain can be recovered.
   */
  private readonly deathLedger: LineState = makeDeathLedger();
  private harnessDriven = false;
  private deathShownFor = -1;
  /** AMD-55 — pending defeat panel while the death beat plays, and the report it waits on. */
  private defeatWait: ReturnType<typeof setTimeout> | null = null;
  private pendingDefeat: DefeatReport | null = null;
  /** True once the beat has played for this run; later presents skip the wait. */
  private defeatBeatDone = false;
  private boundKey: (e: KeyboardEvent) => void;
  private boundBlur: () => void;
  private boundResize: () => void;
  private boundPointer: (e: Event) => void;
  private boundTouch: () => void;
  private boundPad: () => void;

  /** Integrator subscribes here for every user intent (§13.2 flow map edges). */
  onAction: (action: UIAction) => void = () => {};

  // screens, typed for direct access
  readonly title: TitleScreen;
  readonly loadout: LoadoutScreen;
  /** AMD-49 — the meta layer's window. Reads `meta.v1`; never writes it. */
  readonly archive: ArchiveScreen;
  readonly settingsScreen: SettingsScreen;
  readonly pause: PauseScreen;
  readonly levelup: LevelUpScreen;
  readonly victory: VictoryScreen;
  readonly defeat: DefeatScreen;
  readonly summary: SummaryScreen;
  readonly resumePrompt: ResumePromptScreen;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('ui-root');
    this.store = new SettingsStore();

    const emit = (a: UIAction): void => this.dispatch(a);

    this.hud = new Hud();
    // AMD-35 — the tap becomes an action. `performPull` mutates `World`, which the UI
    // may not touch (INTERFACES.md ground rule 4), so the integrator answers this.
    this.hud.onGraft = () => this.dispatch({ type: 'graft_pull' });
    this.onboarding = new Onboarding();
    this.micro = new Microinteractions();

    const hudLayer = div('layer layer-hud');
    hudLayer.appendChild(this.hud.root);
    hudLayer.appendChild(this.onboarding.root);
    this.root.appendChild(hudLayer);

    // The captain gets its own layer between the HUD and the screens. Not in
    // the HUD layer, because the bubble is transient and must never enter `hudZones`:
    // it is not chrome the damage numbers have to dodge, it is a thing that appears
    // for three seconds and leaves.
    const captainLayer = div('layer layer-captain');
    captainLayer.appendChild(this.speech.root);
    this.root.appendChild(captainLayer);

    this.screenLayer = div('layer layer-screens');
    this.root.appendChild(this.screenLayer);
    this.root.appendChild(this.micro.root);

    this.lastSeed = randomSeed();
    this.title = new TitleScreen(emit, VERSION, typeof window !== 'undefined' && window.opener !== null);
    this.loadout = new LoadoutScreen(emit, this.lastSeed, this.store.value.targeting_policy_default);
    this.archive = new ArchiveScreen(emit);
    this.settingsScreen = new SettingsScreen(emit, this.store, (s) => this.applySettings(s));
    this.pause = new PauseScreen(emit);
    this.levelup = new LevelUpScreen(emit);
    this.victory = new VictoryScreen(emit);
    this.defeat = new DefeatScreen(emit);
    this.summary = new SummaryScreen(emit);
    this.resumePrompt = new ResumePromptScreen(emit);

    this.register(this.title);
    this.register(this.loadout);
    this.register(this.archive);
    this.register(this.settingsScreen);
    this.register(this.pause);
    this.register(this.levelup);
    this.register(this.victory);
    this.register(this.defeat);
    this.register(this.summary);
    this.register(this.resumePrompt);

    this.nav.setHost({
      onConfirm: () => {},
      onBack: () => this.back(),
      onAlt: () => {
        if (this.current === 'levelup') this.dispatch({ type: 'reroll' });
      },
      onStart: () => {
        if (this.current === 'run') this.dispatch({ type: 'pause' });
        else if (this.current === 'pause') this.dispatch({ type: 'resume' });
      },
    });

    this.boundKey = (e) => this.handleKey(e);
    this.boundBlur = () => this.handleBlur();
    this.boundResize = () => this.applyScale();
    this.boundPointer = (e) => this.nav.syncFromElement(e.target as Element | null);
    this.boundTouch = () => this.setInputMethod('touch');
    this.boundPad = () => this.setInputMethod('gamepad');

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.boundKey);
      window.addEventListener('blur', this.boundBlur);
      window.addEventListener('resize', this.boundResize);
      window.addEventListener('gamepadconnected', this.boundPad);
      window.addEventListener('touchstart', this.boundTouch, { passive: true, once: true });
      this.root.addEventListener('pointerdown', this.boundPointer);
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.handleBlur();
        });
      }
      if (typeof matchMedia === 'function') {
        const mq = matchMedia('(prefers-reduced-motion: reduce)');
        this.prefersReducedMotion = mq.matches;
        if (typeof mq.addEventListener === 'function') {
          mq.addEventListener('change', (e) => {
            this.prefersReducedMotion = e.matches;
            this.applySettings(this.store.value);
          });
        }
        if (matchMedia('(pointer: coarse)').matches) this.inputMethod = 'touch';
      }
    }

    this.applySettings(this.store.value);
    this.applyScale();
    this.onboarding.setInputMethod(this.inputMethod);
    this.setScreen('title');
  }

  get settings(): Settings {
    return this.store.value;
  }

  get screen(): ScreenName {
    return this.current;
  }

  setContentVersion(v: string): void {
    this.contentVersion = v;
  }

  private register(s: Screen): void {
    this.screens.set(s.name, s);
    this.screenLayer.appendChild(s.root);
  }

  // --- world binding -------------------------------------------------------

  /** Subscribes read-only to the bus. Re-binding a new world drops old handlers. */
  bindWorld(w: World): void {
    /*
     * A re-bind is not always a new run. `Game.seekRunTime` fast-forwards the world and
     * then re-binds *the same* world so the renderer and the audio graph can rebuild
     * from it — and a plain `eventLog.reset()` here threw away the entire history the
     * seek had just produced, which is exactly the history the log exists to show.
     * The log therefore only clears when the world object actually changes or when its
     * clock has gone backwards, which is what a genuine restart looks like.
     */
    const sameRun = this.world === w && w.tick >= this.lastBoundTick;
    this.unbind();
    this.world = w;
    this.lastBoundTick = w.tick;
    this.observer.reset();
    this.hud.reset();
    this.micro.clear();
    this.lastSeed = w.captainSeed;
    if (!sameRun) {
      // A genuine restart drops the encounter; a seek's re-bind keeps the one it just
      // spawned. See `Hud.reset`.
      this.hud.detachBoss();
      this.eventLog.reset();
      this.lastPhase = '';
      this.lastWave = 1;
      this.graftsTaken = 0;
      this.ichorEarned = 0;
      // A new run is a new person. `makeCaptain` is pure in `w.runSeed`, so
      // pasting a share string brings back the same captain along with the same run.
      // A seek re-binds the same world and must not swap them mid-run.
      this.captain = makeCaptain(w.captainSeed);
      this.voice = new CaptainVoice(this.captain);
      this.voice.start(this.now());
      this.speech.stop();
      this.speech.setSpeaker(this.captain.name.short);
      this.speech.setCaptain(this.captain);
      this.defeat.resetCaptain();
      this.deathShownFor = -1;
    }

    const bus = w.bus;
    // AMD-13: the log subscribes for the whole run and allocates nothing per event.
    this.unsubs.push(this.eventLog.attach(bus, () => w.tick));
    this.unsubs.push(
      bus.on('phase_change', (p) => {
        this.lastPhase = p.phase_id;
      }),
    );
    this.unsubs.push(
      bus.on('level_up', (p) => {
        this.micro.levelUp(p.level);
      }),
    );
    this.unsubs.push(
      bus.on('levelup_pick', (p) => {
        this.onboarding.noteChoice();
        if (this.voice) this.voice.noteBoon(this.now(), humanizeId(p.picked));
      }),
    );
    this.unsubs.push(
      bus.on('evolution', (p) => {
        this.micro.evolution(p.evolution_id);
      }),
    );
    this.unsubs.push(
      bus.on('pickup', (p) => {
        if (p.kind === 'xp_mote') {
          this.onboarding.noteMote();
          this.micro.pickupTick();
        }
      }),
    );
    this.unsubs.push(
      bus.on('surge_change', (p) => {
        this.observer.noteSurge(p.to);
        if (p.to > p.from) this.micro.surgeUp(p.to);
      }),
    );
    this.unsubs.push(
      bus.on('dash', (p) => {
        if (p.phase === 'start') this.onboarding.noteDash();
      }),
    );
    this.unsubs.push(
      bus.on('player_hit', (p) => {
        this.observer.notePlayerHit(w.tick, p.amount, p.source_def_id);
        if (this.voice) this.voice.noteHit(this.now(), p.amount, sourceName(p.source_def_id));
      }),
    );
    this.unsubs.push(
      bus.on('boss_spawn', (p) => {
        this.hud.attachBoss(w, p.entity_id, p.boss_id);
        const def = bossById.get(p.boss_id);
        if (this.voice) this.voice.noteBoss(this.now(), def ? String(def.display_name) : humanizeId(p.boss_id));
        this.hud.showBossIntro(
          def ? String(def.display_name) : p.boss_id,
          BOSS_EPITHETS[p.boss_id] ?? '',
          BOSS_INTRO_MS,
          this.now(),
        );
      }),
    );
    this.unsubs.push(
      bus.on('boss_defeated', (p) => {
        const def = bossById.get(p.boss_id);
        this.micro.bossDefeated(def ? String(def.display_name) : p.boss_id);
        // Only if this is the encounter the bar is showing: another boss may still be alive.
        this.hud.detachBoss(p.boss_id);
        if (this.voice) this.voice.noteBossDown(this.now());
      }),
    );
    // AMD-34 — the wave is the run's unit of progress, so the summary and the log both
    // want it. The HUD reads the director's queries directly instead of these events:
    // `wave_phase` fires once per beat and the muster brace counts down every frame.
    this.unsubs.push(
      bus.on('wave_change', (p) => {
        this.lastWave = p.wave;
        // Wave 1 opens with the run; the boarding line already owns that beat.
        if (this.voice && p.wave > 1) this.voice.noteWave(this.now());
      }),
    );
    // AMD-35 — the Graft. `gacha_ready` pulses the button once on the rising edge of
    // affordability; `gacha_pull` raises the reveal, which never pauses the sim.
    this.unsubs.push(
      bus.on('gacha_ready', () => {
        this.hud.pulseGraftReady();
      }),
    );
    this.unsubs.push(
      bus.on('gacha_pull', (p) => {
        this.graftsTaken++;
        this.hud.showGraftReveal(p, this.now());
        if (this.voice) this.voice.noteBoon(this.now(), p.display_name);
      }),
    );
    // AMD-46 — §14.1 B3 refused part of a split boon's burst. The HUD marks the bay it
    // was refused from and, at most once every few seconds, says the count out loud.
    // Nothing here mutates the sim; the budget already made its decision.
    this.unsubs.push(
      bus.on('budget_trim', (p) => {
        this.hud.noteBudgetTrim(p.source, p.weapon_id, p.requested, p.spawned, this.now());
      }),
    );
    this.unsubs.push(
      bus.on('gacha_ichor', (p) => {
        // The event fires on whole-unit crossings only, so summing `amount` is the
        // run's gross income — a figure the balance alone cannot give once a Graft
        // has been paid for.
        this.ichorEarned += p.amount;
      }),
    );
    this.unsubs.push(
      bus.on('game_over', (p) => {
        // Silence first. A half-typed ambient line must not still be crawling across
        // the screen while the death card types the last thing they ever said.
        if (this.voice) this.voice.silence();
        this.speech.stop();
        if (p.outcome !== 'defeat') {
          this.presentVictory(w);
          return;
        }
        // AMD-55 — the panel waits for the ship to finish exploding. `presentDefeat` owns
        // the wait, so it does not matter whether this handler or the integrator's own
        // `game_over` subscriber reaches it first.
        this.presentDefeat(w);
      }),
    );

    if (this.store.value.gameplay.onboarding_seen) this.onboarding.skip();
    else this.onboarding.start();
  }

  private unbind(): void {
    for (let i = 0; i < this.unsubs.length; i++) this.unsubs[i]();
    this.unsubs.length = 0;
    this.clearDefeatWait();
  }

  /** A restart during the beat must not open a defeat panel over the new run. */
  private clearDefeatWait(): void {
    if (this.defeatWait !== null) {
      clearTimeout(this.defeatWait);
      this.defeatWait = null;
    }
    this.pendingDefeat = null;
    this.defeatBeatDone = false;
  }

  // --- screens -------------------------------------------------------------

  setScreen(name: ScreenName): void {
    if (this.started && this.current === name) return;
    this.started = true;
    const prev = this.screens.get(this.current);
    if (prev) prev.hide();
    this.current = name;

    const isRun = name === 'run';
    this.root.dataset.screen = name;
    this.hud.root.hidden = !isRun;
    // A bubble left hanging under a modal reads as a bug. Leaving the run screen kills
    // the line outright rather than fading it: whatever they were saying is over.
    if (!isRun) this.speech.stop();
    this.hud.invalidateZones();
    this.onboarding.root.hidden = this.onboarding.root.hidden || !isRun;

    if (isRun) {
      this.nav.setStops([], false);
      this.nav.stopPolling();
      return;
    }
    const next = this.screens.get(name);
    if (!next) return;
    next.show();
    this.nav.setStops(next.focusStops(), true);
    this.nav.startPolling();
  }

  /** Called by the integrator when R5 has produced offers (§13.1.6). */
  presentLevelUp(state: LevelUpState): void {
    this.levelup.present(state, this.now());
    this.setScreen('levelup');
  }

  presentPause(w: World): void {
    this.pause.refresh(w);
    this.pause.presentLog(this.eventLog, this.statusFields(w.runSeed, w.tick, w.difficultyId));
    this.setScreen('pause');
  }

  /** AMD-13 — the run's coordinates, shared by every status line. */
  private statusFields(seed: number, tick: number, difficultyId: string): StatusFields {
    return {
      seed,
      tick,
      arena_id: this.lastArena,
      phase_id: this.lastPhase,
      difficulty_id: difficultyId,
      content_version: this.contentVersion,
    };
  }

  presentVictory(w: World): void {
    this.victory.setReducedFlashes(this.reducedMotion);
    // The survivor gets the same card the dead get. `deathLine` is null here: they are
    // still alive, and a last word would be a lie.
    if (this.captain !== null) this.victory.presentCaptain(this.captain, w.tick / 60);
    this.victory.present(w);
    this.setScreen('victory');
  }

  /** Builds the §13.1.9 report from the observer unless one is supplied. */
  /**
   * AMD-55 — the defeat panel waits for the ship to finish exploding.
   *
   * It used to open on the tick the hull failed, which drew a full-screen card over the
   * one second the death beat exists in: nobody ever saw the rupture. Two call sites reach
   * here on that tick — this class's own `game_over` handler and the integrator's, which
   * carries the §13.1.9 report — and their order is not fixed, so the wait lives here
   * rather than in either of them. Whichever arrives first starts the beat window; the
   * other only leaves its report behind. After the window, calls present immediately,
   * which is what a re-present with a fuller report needs.
   */
  presentDefeat(w: World, report?: DefeatReport): void {
    if (report !== undefined) this.pendingDefeat = report;
    if (this.defeatBeatDone) {
      this.presentDefeatNow(w, this.pendingDefeat ?? undefined);
      return;
    }
    if (this.defeatWait !== null) return;
    this.defeatWait = setTimeout(() => {
      this.defeatWait = null;
      this.defeatBeatDone = true;
      this.presentDefeatNow(w, this.pendingDefeat ?? undefined);
    }, DEATH_BEAT_MS);
  }

  private presentDefeatNow(w: World, report?: DefeatReport): void {
    // The card first, so it is painted before the panel's first frame.
    // `takeDeathLine` consumes from the session ledger, which is why it must run once
    // per run and not once per call site: the integrator presents defeat a second time
    // with an explicit report.
    const c = this.captain;
    if (c !== null && this.deathShownFor !== (w.runSeed >>> 0)) {
      this.deathShownFor = w.runSeed >>> 0;
      const last = this.voice !== null ? this.voice.deathLine(this.deathLedger) : null;
      this.defeat.setReducedMotion(this.reducedMotion);
      this.defeat.presentCaptain(c, last, w.tick / 60);
    }
    const r = report ?? this.observer.build(w);
    this.defeat.present(r, w.runSeed, w.arenaHalfW, w.arenaHalfH);
    this.setScreen('defeat');
  }

  presentSummary(s: RunSummary): void {
    // AMD-34/35 — the integrator builds the summary from `World.counters`, which carry
    // neither the wave nor the Graft ledger. Fill them in from what the UI watched go
    // past, and leave anything the integrator did supply alone.
    if (s.wave === undefined) s.wave = this.lastWave;
    if (s.ichor_earned === undefined) s.ichor_earned = this.ichorEarned;
    if (s.grafts === undefined) s.grafts = this.graftsTaken;
    this.summary.present(s);
    this.summary.presentLog(this.eventLog, this.statusFields(s.seed, Math.round(s.duration_s * 60), s.difficulty_id));
    this.setScreen('summary');
  }

  setResumeAvailable(info: ResumeInfo | null): void {
    this.title.setResumeAvailable(info !== null);
    if (info) this.resumePrompt.present(info);
    if (this.current === 'title') this.nav.setStops(this.title.focusStops(), false);
  }

  /** §13.1.7 framing, when the integrator wants it outside the `boss_spawn` event. */
  showBossIntro(bossId: string): void {
    const def = bossById.get(bossId);
    this.hud.showBossIntro(def ? String(def.display_name) : bossId, BOSS_EPITHETS[bossId] ?? '', BOSS_INTRO_MS, this.now());
  }

  /** Summary content version stamp for the share string. */
  get version(): string {
    return this.contentVersion;
  }

  // --- per-frame -----------------------------------------------------------

  /** Read-only. Called once per rendered frame from the integrator's render step. */
  update(w: World): void {
    if (this.current !== 'run' && this.current !== 'levelup' && this.current !== 'pause') return;
    const now = this.now();
    this.hud.update(w, now);
    // The captain only talks while the run is on screen. A paused or
    // level-up-modal frame must not spend a slot: `poll` is cheap but the pacing model
    // measures wall time, and a 40 s deliberation over three cards would otherwise
    // come back to a captain who has been silent for the whole of it.
    if (this.current === 'run' && this.voice !== null && !w.paused) {
      const maxHp = w.player.max_hp > 0 ? w.player.max_hp : 1;
      const req = this.voice.poll(now, {
        runTimeS: w.tick / 60,
        hpFrac: Math.max(0, Math.min(1, w.player.hp / maxHp)),
        enemiesAlive: w.counters.enemies_alive,
      });
      if (req !== null) this.speech.say(req.text);
    }
    if (this.current === 'run' && this.onboarding.isActive) {
      this.onboarding.noteMovement(w.player.pos.x, w.player.pos.y);
      this.onboarding.update(w.tick / 60);
      // The last beat closes itself; record it so returning profiles see 0 overlays.
      if (!this.onboarding.isActive) this.markOnboardingSeen();
    }
  }

  // --- settings ------------------------------------------------------------

  private applySettings(s: Settings): void {
    const r = this.root;
    r.dataset.colorblind = s.video.colorblind_mode;
    r.style.setProperty('--ui-text-scale', String(s.video.text_scale));
    /*
     * A flag rather than a media query: CSS cannot branch on a custom property, and the
     * HUD's margin rule needs to know when text has grown enough to need the width back.
     *
     * The trigger is ANY enlarged text, not just the largest step. The first cut fired at
     * 1.4 and a sweep across the four §13.4 steps caught the hole: at 1.3 with a full
     * eight-item build the cluster stood at 202 px, worse than 1.5 was WITH the wide band.
     * Every step above 1.0 grows the bays, and the step that grows them without the room
     * to hold them is the one that hurts.
     */
    r.dataset.textScale = s.video.text_scale > 1 ? 'large' : 'normal';
    r.style.setProperty('--ui-scanline', String(s.video.scanline_intensity));
    r.classList.toggle('reduced-flashes', s.video.reduced_flashes);
    r.classList.toggle('reduced-particles', s.video.reduced_particles);
    r.classList.toggle('reduced-shake', s.video.reduced_shake);
    r.classList.toggle('reduced-motion', this.reducedMotion);
    this.micro.setReducedMotion(this.reducedMotion);
    // Reduced motion reveals a line whole. The typewriter is cadence, not
    // information, and a player who asked for less movement did not ask for less text.
    this.speech.setReducedMotion(this.reducedMotion);
    this.defeat.setReducedMotion(this.reducedMotion);
    this.victory.setReducedFlashes(s.video.reduced_flashes);
    // §13.4 text scale resizes every plate, and the radar plus both HUD banners are
    // positioned off the measured cluster.
    this.hud.invalidateZones();
  }

  get reducedMotion(): boolean {
    return this.prefersReducedMotion || this.store.value.video.reduced_flashes;
  }

  /** §13.5 + AMD-1: `s = clamp(min(vw, vh) / 390, 0.85, 3.0)`. */
  private applyScale(): void {
    if (typeof window === 'undefined') return;
    const min = Math.min(window.innerWidth, window.innerHeight);
    const s = Math.max(HUD_SCALE_MIN, Math.min(HUD_SCALE_MAX, min / HUD_SCALE_BASE));
    this.root.style.setProperty('--hud-scale', s.toFixed(3));
    this.root.classList.toggle('is-landscape', window.innerWidth > window.innerHeight);
    this.hud.invalidateZones();
  }

  /**
   * D8 / D14 — the rectangles the HUD occupies, in CSS px viewport coordinates.
   *
   * R6 reads this to keep world-space marks out of the chrome: damage numbers clamp
   * out of `zones.top` (a "70" used to land on the run timer) and off-screen threat
   * chevrons live inside `zones.edgeBand` along the left, right and bottom edges,
   * which the HUD now leaves clear unconditionally — AMD-48 deleted the dash button,
   * which was the one element allowed to paint there, and its published rect with it.
   *
   * Cheap to read every frame — the measurement only re-runs after a resize or a
   * plate visibility change. See `src/ui/layout.ts` for the helpers.
   */
  get hudZones(): HudReservedZones {
    return this.hud.zones;
  }

  setInputMethod(m: InputMethod): void {
    if (this.inputMethod === m) return;
    this.inputMethod = m;
    this.root.dataset.input = m;
    this.onboarding.setInputMethod(m);
  }

  get currentInputMethod(): InputMethod {
    return this.inputMethod;
  }

  /** Marks onboarding complete for this profile (persists immediately). */
  markOnboardingSeen(): void {
    this.store.update((s) => {
      s.gameplay.onboarding_seen = true;
    });
  }

  // --- input ---------------------------------------------------------------

  private handleKey(e: KeyboardEvent): void {
    if (this.settingsScreen.isCapturing && this.current === 'settings') {
      if (this.settingsScreen.captureKey(e)) return;
    }
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
      if (e.key !== 'Escape') this.setInputMethod('keyboard');
    }

    if (e.code === 'Escape') {
      e.preventDefault();
      this.back();
      return;
    }

    if (this.current === 'levelup') {
      if (this.levelup.handleKey(e)) {
        e.preventDefault();
        return;
      }
    }

    if (this.current === 'run') return;

    if (this.current === 'title' && isPlainKey(e)) {
      e.preventDefault();
      this.dispatch({ type: 'goto', screen: 'loadout' });
      return;
    }

    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    if (this.nav.handleKey(e)) e.preventDefault();
  }

  private back(): void {
    if (this.current === 'run') {
      this.dispatch({ type: 'pause' });
      return;
    }
    const s = this.screens.get(this.current);
    if (s && s.onBack()) return;
    this.dispatch({ type: 'goto', screen: 'title' });
  }

  /** §13.4 pause on focus loss: blur halts the sim within a tick. */
  private handleBlur(): void {
    if (this.current !== 'run') return;
    if (this.harnessDriven) return; // see `setHarnessDriven`
    if (!this.store.value.access.pause_on_focus_loss) return;
    this.dispatch({ type: 'pause' });
  }

  /**
   * Capture, bench and the A/B review lab drive the page instead of playing it, and the
   * lab runs two builds in two iframes where only one can hold focus. Both this handler
   * and `Game.handleFocusLoss` reach the same `pauseRun`, so both have to know: an
   * unfocused harness page is not a player who looked away.
   */
  setHarnessDriven(on: boolean): void {
    this.harnessDriven = on;
  }

  private dispatch(a: UIAction): void {
    if (a.type === 'goto') {
      if (a.screen === 'settings') this.settingsScreen.setReturnTo(this.current === 'pause' ? 'pause' : 'title');
      if (a.screen === 'loadout') this.loadout.setSeed(this.lastSeed);
      this.setScreen(a.screen);
    }
    if (a.type === 'start_run') {
      this.lastSeed = a.loadout.seed;
      this.lastArena = a.loadout.arena_id;
    }
    if (a.type === 'retry_same_seed') this.lastSeed = a.seed;
    if (a.type === 'new_run') this.lastSeed = randomSeed();
    this.onAction(a);
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  /** Full teardown — used by tests and hot reload. */
  destroy(): void {
    this.unbind();
    this.speech.dispose();
    this.voice = null;
    this.captain = null;
    this.nav.stopPolling();
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.boundKey);
      window.removeEventListener('blur', this.boundBlur);
      window.removeEventListener('resize', this.boundResize);
      window.removeEventListener('gamepadconnected', this.boundPad);
      this.root.removeEventListener('pointerdown', this.boundPointer);
    }
    this.world = null;
  }

  /** Exposed so the integrator can read the world the UI is observing. */
  get boundWorld(): World | null {
    return this.world;
  }
}

/**
 * What the captain calls the thing that hit them. Mirrors `RunObserver`'s
 * own resolution so the death card and the run memory agree on a name; an id that
 * resolves to nothing is dropped rather than spoken, because a captain saying
 * `enemy.driftfin` out loud is worse than a captain saying nothing.
 */
function sourceName(defId: string): string {
  if (!defId) return '';
  const e = enemyById.get(defId);
  if (e) return String(e.display_name);
  const b = bossById.get(defId);
  if (b) return String(b.display_name);
  return humanizeId(defId);
}

/** A key that should trigger "any key = Descend" on the title (§13.1.1). */
function isPlainKey(e: KeyboardEvent): boolean {
  if (e.metaKey || e.ctrlKey || e.altKey) return false;
  switch (e.key) {
    case 'Tab':
    case 'Shift':
    case 'Control':
    case 'Alt':
    case 'Meta':
    case 'ArrowUp':
    case 'ArrowDown':
    case 'ArrowLeft':
    case 'ArrowRight':
    case 'Escape':
    case 'Enter':
    case ' ':
      return false;
    default:
      return e.key.length === 1;
  }
}
